import React from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.regions.min.js';
import { formatSeconds, revWindow } from '@revdotcom/common';
import { TranscribeButton } from './TranscribeButton';
import { PreviewState } from './AudioToolsModels';
/**
 * Tool that allows user to preview their audio.
 */
export class PreviewAudio extends React.Component {
    constructor(props) {
        super(props);
        this.stereoNumChannels = 2;
        // Synchronous re-entry guard. setState is async, so we can't gate clicks
        // on state — rapid taps on mobile would each pass the check before the
        // first setState lands, spawning concurrent workers and crashing the tool.
        this.downloadInFlight = false;
        this.onReadyCallback = (wave) => {
            this.wave = wave;
            if (this.props.interactive) {
                this.initRegion();
            }
            this.setState({
                previewDuration: wave.getDuration(),
            });
        };
        this.onWaveformReadyCallback = () => {
            this.setState({
                previewState: PreviewState.Ready,
            });
        };
        this.initRegion = () => {
            const options = {
                start: 0,
                end: this.wave.getDuration(),
                loop: false,
                color: 'rgba(0, 0, 0, 0)',
                handleStyle: {
                    left: {
                        backgroundColor: '#fff',
                    },
                    right: {
                        backgroundColor: '#fff',
                    },
                },
            };
            this.region = this.wave.addRegion(options);
            this.createDimOverlays();
            this.updateDimOverlays();
            this.wave.on('region-updated', this.updateDimOverlays);
        };
        // Dim the areas outside the selected region (wavesurfer's region color only
        // paints inside, so we add sibling overlays and track the region edges).
        this.createDimOverlays = () => {
            const waveContainer = document.getElementById('waveform-interactive');
            const waveEl = waveContainer === null || waveContainer === void 0 ? void 0 : waveContainer.querySelector('wave');
            if (!waveEl)
                return;
            // Start with width:0 so overlays are invisible until properly sized
            const baseStyle = 'position:absolute;top:0;bottom:0;background:rgba(0,0,0,0.5);pointer-events:none;z-index:2;width:0;';
            this.leftDimOverlay = document.createElement('div');
            this.leftDimOverlay.style.cssText = baseStyle + 'left:0;';
            waveEl.appendChild(this.leftDimOverlay);
            this.rightDimOverlay = document.createElement('div');
            this.rightDimOverlay.style.cssText = baseStyle + 'right:0;';
            waveEl.appendChild(this.rightDimOverlay);
        };
        this.updateDimOverlays = () => {
            if (!this.region || !this.wave || !this.leftDimOverlay || !this.rightDimOverlay)
                return;
            const duration = this.wave.getDuration();
            if (!duration || duration <= 0)
                return;
            // Clamp values to valid range to prevent visual glitches
            const start = Math.max(0, Math.min(this.region.start, duration));
            const end = Math.max(start, Math.min(this.region.end, duration));
            const startPct = (start / duration) * 100;
            const endPct = (end / duration) * 100;
            this.leftDimOverlay.style.width = `${startPct}%`;
            this.rightDimOverlay.style.width = `${100 - endPct}%`;
        };
        /** Hide dim overlays during upload to prevent visual glitches */
        this.hideDimOverlays = () => {
            // Unbind the region-updated listener to prevent any internal WaveSurfer
            // events from triggering overlay updates during transcribe/upload
            if (this.wave) {
                this.wave.un('region-updated', this.updateDimOverlays);
            }
            if (this.leftDimOverlay) {
                this.leftDimOverlay.style.display = 'none';
            }
            if (this.rightDimOverlay) {
                this.rightDimOverlay.style.display = 'none';
            }
            // Force a waveform redraw after layout settles to prevent width issues
            // from the button changing to upload state
            requestAnimationFrame(() => {
                if (this.wave) {
                    this.wave.drawBuffer();
                }
            });
        };
        /** Show dim overlays after upload completes */
        this.showDimOverlays = () => {
            if (this.leftDimOverlay) {
                this.leftDimOverlay.style.display = '';
            }
            if (this.rightDimOverlay) {
                this.rightDimOverlay.style.display = '';
            }
            // Re-bind the region-updated listener and update overlays to current state
            if (this.wave) {
                // Unbind first to prevent duplicate listeners if called multiple times
                this.wave.un('region-updated', this.updateDimOverlays);
                this.wave.on('region-updated', this.updateDimOverlays);
                this.updateDimOverlays();
                // Redraw waveform to ensure correct dimensions
                this.wave.drawBuffer();
            }
        };
        // Minimum ms to keep the download button in its "busy" state, even if the
        // trim completes faster. On short clips, the work finishes in <100ms — too
        // quick for the user to see their tap registered. Pinning the busy class
        // makes rapid taps visibly bounce off the button.
        this.minDownloadBusyMs = 600;
        this.handleDownloadClick = () => {
            if (this.downloadInFlight)
                return;
            this.downloadInFlight = true;
            const startedAt = Date.now();
            this.forceUpdate();
            window.analytics.track('Button Click', {
                altEventName: `${this.props.segmentAudioToolName}: Download`,
                stateChange: 'None',
                locationUrl: revWindow.baseUrl(),
                buttonText: '[Download Arrow]',
                buttonLocation: 'Main Page (after recording or file uploaded)',
            });
            this.setState({
                message: 'Trimming your file...',
            }, async () => {
                try {
                    await this.handleDownload();
                    this.setState({ message: '' });
                    this.props.downloadCallback();
                }
                catch (error) {
                    console.error('[PreviewAudio] Error during download:', error);
                    console.error('[PreviewAudio] Error message:', error instanceof Error ? error.message : String(error));
                    console.error('[PreviewAudio] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
                    this.setState({
                        message: 'An error occurred: please try again',
                    });
                }
                finally {
                    const elapsed = Date.now() - startedAt;
                    const remaining = Math.max(0, this.minDownloadBusyMs - elapsed);
                    setTimeout(() => {
                        this.downloadInFlight = false;
                        this.forceUpdate();
                    }, remaining);
                }
            });
        };
        this.handleDownload = async () => {
            const url = await this.getAppropriateUrl();
            this.downloadMp3(url);
        };
        this.downloadMp3 = (url) => {
            const downloadBtn = document.createElement('a');
            downloadBtn.href = url;
            downloadBtn.download = `${this.props.fileNameWithoutExtension}.mp3`;
            downloadBtn.click();
        };
        this.onAudioProcessCallback = (wave) => {
            const currentTime = wave.getCurrentTime();
            if (!this.isCurrentTimeInRegion(currentTime)) {
                this.pausePreview();
            }
            this.setState({ previewTime: currentTime });
        };
        this.onSeekCallback = (wave, progress) => {
            this.setState({
                previewTime: progress * wave.getDuration(),
            });
        };
        this.onFinishCallback = (wave) => {
            wave.stop();
            this.setState({
                previewTime: 0,
                previewState: PreviewState.Ready,
            });
        };
        this.onErrorCallback = () => {
            this.setState({ previewState: PreviewState.Error });
        };
        this.pausePreview = () => {
            this.wave.pause();
            this.setState({
                previewState: PreviewState.Paused,
            });
        };
        this.isCurrentTimeInRegion = (currentTime) => {
            return !this.region || (currentTime >= this.region.start && currentTime < this.region.end);
        };
        this.playPreview = () => {
            const currentTime = this.wave.getCurrentTime();
            const start = this.isCurrentTimeInRegion(currentTime) ? currentTime : this.getRegionStartWithinRange();
            if (this.isCurrentTimeInRegion(start)) {
                this.wave.play(start);
                this.setState({
                    previewState: PreviewState.Playing,
                });
            }
        };
        this.warnBeforeDeleting = () => {
            if (this.wave != null) {
                this.wave.stop();
            }
            this.props.warnBeforeDeleting();
        };
        this.skipBackward = () => {
            this.wave.skipBackward(15);
        };
        this.skipForward = () => {
            if (this.wave.getCurrentTime() + 15 > this.wave.getDuration()) {
                this.pausePreview();
            }
            this.wave.skipForward(15);
        };
        this.getAppropriateUrl = async () => {
            let url = this.props.urlDownloadLink;
            if (this.region) {
                const trimmedMp3Buffer = await this.trimAndEncodeBufferAsync();
                url = this.props.mp3Helper.generateDownloadableUrl([trimmedMp3Buffer]);
            }
            return url;
        };
        this.trimAndEncodeBufferAsync = () => {
            return new Promise((resolve, reject) => {
                const worker = new Worker(new URL('./Trimmer.worker.ts', import.meta.url));
                const trimAndEncodeOptions = this.buildTrimAndEncodeOptions();
                worker.onmessage = (e) => {
                    worker.terminate();
                    resolve(e.data);
                };
                worker.onerror = (e) => {
                    console.error('[PreviewAudio] Worker error event:', e);
                    console.error('[PreviewAudio] Worker error message:', e.message);
                    console.error('[PreviewAudio] Worker error filename:', e.filename);
                    console.error('[PreviewAudio] Worker error lineno:', e.lineno);
                    worker.terminate();
                    reject(e);
                };
                worker.postMessage({
                    encoderOptions: this.props.mp3Helper.encoderOptions,
                    trimAndEncodeOptions: trimAndEncodeOptions,
                });
            });
        };
        /**
         * Region start and onAudioProcessCallback use different floating
         * point precision so we need to ensure that the region start we use
         * is moved to just past the actual start value to prevent play automatically
         * triggering a pause call.
         */
        this.getRegionStartWithinRange = () => {
            return Math.ceil(this.region.start * 1000) / 1000 + 0.01;
        };
        this.buildTrimAndEncodeOptions = () => {
            const buffer = this.wave.backend.buffer;
            const channelDataDependentOnChannelNumber = buffer.numberOfChannels >= this.stereoNumChannels
                ? [buffer.getChannelData(0), buffer.getChannelData(1)]
                : [buffer.getChannelData(0)];
            return {
                channelData: channelDataDependentOnChannelNumber,
                regionStart: this.region.start,
                regionEnd: this.region.end,
            };
        };
        this.getTrimmedFile = async () => {
            const trimmedMp3Buffer = await this.trimAndEncodeBufferAsync();
            const finalBlob = this.props.mp3Helper.generateMp3Blob([trimmedMp3Buffer]);
            return this.props.mp3Helper.generateMp3FileFromBlob(finalBlob, `${this.props.fileNameWithoutExtension}.mp3`);
        };
        this.getAudioLengthMinutes = () => {
            return (this.region.end - this.region.start) / 60.0;
        };
        this.renderAudioTimer = () => {
            const displayTime = formatSeconds(this.state.previewTime, true);
            return (React.createElement("div", { id: "preview-timer", className: "white tracked mt2 fw5 f6" }, displayTime));
        };
        this.setMessage = (message) => {
            this.setState({ message: message });
        };
        this.renderInteractionButtons = () => {
            var _a;
            const message = this.state.message.length > 0 ? React.createElement("div", null, this.state.message) : null;
            return (React.createElement("div", null,
                React.createElement("div", { className: "flex flex-row items-center justify-center gap-3 mv4" },
                    React.createElement("a", { title: "Download", id: "download-icon-desktop", className: `download-icon pointer${this.downloadInFlight ? ' is-busy' : ''}`, "aria-disabled": this.downloadInFlight, onClick: this.handleDownloadClick }),
                    React.createElement(TranscribeButton, { awsUploadInfo: this.props.awsUploadInfo, isAutomatedTranscription: (_a = this.props.isAutomatedTranscription) !== null && _a !== void 0 ? _a : false, getTrimmedFile: this.getTrimmedFile, getAudioLengthMinutes: this.getAudioLengthMinutes, setMessage: this.setMessage, segmentAudioToolName: this.props.segmentAudioToolName, onUploadComplete: this.props.onUploadComplete, onTranscribeStart: this.hideDimOverlays, onTranscribeEnd: this.showDimOverlays }),
                    React.createElement("div", { title: "Delete", id: "delete-icon", className: "delete-icon pointer", onClick: this.warnBeforeDeleting })),
                message && (React.createElement("div", { id: "message", className: "ph2 tc white fw6 tracked mt3" }, message))));
        };
        this.renderPlayPauseButton = () => {
            switch (this.state.previewState) {
                case PreviewState.Ready:
                case PreviewState.Paused:
                    return (React.createElement("div", { title: "Play audio", className: "play-icon-preview mh4 dib pointer", onClick: this.playPreview }));
                case PreviewState.Playing:
                    return React.createElement("div", { title: "Pause", className: "pause-icon mh4 dib pointer", onClick: this.pausePreview });
                case PreviewState.Loading:
                    return (React.createElement("button", { disabled: true, className: "play-icon-preview outline-0 bg-transparent mh4 dib pointer", onClick: this.playPreview }));
                case PreviewState.Error:
                default:
                    return null;
            }
        };
        this.renderPreviewButtons = () => {
            const disableButton = this.state.previewState === PreviewState.Loading;
            return (React.createElement("div", { id: "preview-buttons-container" },
                this.renderAudioTimer(),
                React.createElement("div", { className: "flex flex-row justify-center items-center mv2" },
                    React.createElement("button", { title: "Rewind 15 seconds", disabled: disableButton, className: "rewind-icon outline-0 bg-transparent dib pointer", onClick: this.skipBackward }),
                    this.renderPlayPauseButton(),
                    React.createElement("button", { title: "Fast forward 15 seconds", disabled: disableButton, className: "forward-icon outline-0 bg-transparent dib pointer", onClick: this.skipForward }))));
        };
        this.renderWaveform = () => {
            return (React.createElement("div", { id: "waveform-container" },
                this.props.interactive && (React.createElement("div", { id: "total-audio-length", className: "flex justify-center white tracked pv2 fw5 f6" }, this.state.previewState === PreviewState.Loading
                    ? 'Loading preview...'
                    : formatSeconds(this.state.previewDuration, true))),
                React.createElement("div", { id: this.props.interactive ? 'waveform-interactive' : 'waveform-plain' }),
                this.props.interactive && this.renderPreviewButtons()));
        };
        this.renderErrorMessage = () => {
            return (React.createElement("div", { id: "wavesurfer-error-message", className: "flex items-center justify-center white h5 mh3 fw5" }, "Previewing your audio is currently not supported on this browser."));
        };
        this.state = {
            previewState: PreviewState.Loading,
            previewTime: 0,
            previewDuration: 0,
            message: '',
        };
        this.wave = null;
        this.region = null;
        this.leftDimOverlay = null;
        this.rightDimOverlay = null;
    }
    componentWillUnmount() {
        var _a, _b;
        if (this.wave) {
            this.wave.un('region-updated', this.updateDimOverlays);
        }
        (_a = this.leftDimOverlay) === null || _a === void 0 ? void 0 : _a.remove();
        (_b = this.rightDimOverlay) === null || _b === void 0 ? void 0 : _b.remove();
    }
    componentDidMount() {
        const waveFormContainerId = this.props.interactive ? 'waveform-interactive' : 'waveform-plain';
        const waveformContainer = document.getElementById(waveFormContainerId);
        if (waveformContainer) {
            const wave = WaveSurfer.create({
                container: waveformContainer,
                height: 140,
                waveColor: 'white',
                progressColor: 'white',
                cursorColor: '#120F20',
                barWidth: 3,
                cursorWidth: this.props.interactive ? 4 : 0,
                barGap: 4,
                barRadius: 2,
                normalize: true,
                responsive: true,
                interact: this.props.interactive,
                hideScrollbar: true,
                backend: 'MediaElement',
                plugins: [RegionsPlugin.create()],
            });
            wave.load(this.props.urlDownloadLink);
            wave.on('ready', () => {
                this.onReadyCallback(wave);
            });
            wave.on('waveform-ready', () => {
                this.onWaveformReadyCallback();
            });
            wave.on('audioprocess', () => {
                this.onAudioProcessCallback(wave);
            });
            wave.on('seek', (progress) => {
                this.onSeekCallback(wave, progress);
            });
            wave.on('finish', () => {
                this.onFinishCallback(wave);
            });
            wave.on('error', () => {
                this.onErrorCallback();
            });
        }
    }
    render() {
        return (React.createElement("div", null,
            this.state.previewState === PreviewState.Error ? this.renderErrorMessage() : this.renderWaveform(),
            this.props.interactive && this.renderInteractionButtons()));
    }
}
